../<template>
  <b-card>
    <div class="row">
      <div class="col-6 d-flex align-items-center justify-content-lg-start">
        <Button
          v-if="showBackButton && actionType === 'save' || 'add'"
          :label="$t('List page')"
          size="md"
          icon="ArrowLeftIcon"
          show-icon
          class="mb-1"
          @click="backNavigationAction"
        />
      </div>
      <div class="col-6 d-flex justify-content-end">
        <Button
          v-if="showSearchButton"
          :label="$t('Search')"
          size="md"
          icon="SearchIcon"
          show-icon
          @click="showSearchField"
        />
      </div>
    </div>
    <b-row class="position-relative">
      <b-col
        v-if="!showSearchButton"
        md="4"
      >
        <validation-observer ref="singleTransactionRules">
          <b-form>
            <h6 class="mb-2 mt-2">
              {{ $t('Account Summary') }}
            </h6>
            <b-row>
              <b-col md="12">
                <InputField
                  id="posting-reference-number"
                  v-model="searchParams.postingReferenceNumber"
                  :label="$t('Posting Reference Number')"
                  :placeholder="$t('Enter posting reference number')"
                  type="text"
                  rules="required"
                  :error-name="$t('Posting Reference Number')"
                />
              </b-col>

              <b-col md="12">
                <Button
                  :label="$t('Search')"
                  size="md"
                  class="mt-2"
                  block
                  @click="handleSearch"
                />
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>

      </b-col>

      <b-col
        v-if="showSearchButton"
        :md="showSearchButton === true ? '12' : '8'"
      >
        <div v-if="showCustomerInfo">

          <div class="mt-4">
            <h6 class="mb-2">
              {{ $t('Transaction to be Reversed') }}
            </h6>
            <b-table
              ref="transactionToBeReversedRef"
              :items="transactionToBeReversedList"
              responsive
              :per-page="pageSize"
              :current-page="currentPage"
              :fields="translatedTransactionToBeReversedTableHeaders"
              primary-key="id"
              show-empty
              :empty-text="$t('No records available')"
              class="position-relative"
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner
                    class="align-middle"
                    variant="primary"
                  />
                </div>
              </template>

              <template #cell(SN)="data">
                {{ getOverallIndex(data.index) }}
              </template>

              <template #cell(financialDate)="data">
                {{ toDateWithYear(data.value) }}
              </template>

              <template #cell(transactionDate)="data">
                {{ toDateWithTime(data.value) }}
              </template>

              <template #cell(credit)="data">
                {{ toBalance(data.value) }}
              </template>

              <template #cell(debit)="data">
                {{ toBalance(data.value) }}
              </template>

              <template #cell(amount)="data">
                {{ toBalance(data.value) }}
              </template>

            </b-table>
          </div>

          <div class="mt-4">
            <h6 class="mb-2 mt-2">
              {{ $t('Reversal Outcome') }}
            </h6>
            <b-table
              ref="reversalOutcomeRef"
              :items="reversalOutcomeList"
              responsive
              :per-page="pageSize"
              :current-page="currentPage"
              :fields="translatedReversalOutcomeTableHeaders"
              primary-key="id"
              show-empty
              :empty-text="$t('No records available')"
              class="position-relative"
            >
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner
                    class="align-middle"
                    variant="primary"
                  />
                </div>
              </template>

              <template #cell(SN)="data">
                {{ getOverallIndex(data.index) }}
              </template>

              <template #cell(financialDate)="data">
                {{ toDateWithYear(data.value) }}
              </template>

              <template #cell(transactionDate)="data">
                {{ toDateWithTime(data.value) }}
              </template>

              <template #cell(credit)="data">
                {{ toBalance(data.value) }}
              </template>

              <template #cell(debit)="data">
                {{ toBalance(data.value) }}
              </template>

              <template #cell(amount)="data">
                {{ toBalance(data.value) }}
              </template>

            </b-table>
          </div>

          <div class="text-center mt-5">
            <Button
              label="Post"
              :loading="isLoading"
              :disabled="isDisabled"
              size="lg"
              @click="handleSubmit"
            />
          </div>

        </div>
      </b-col>
    </b-row>

    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>

import {
  BCard, BRow, BCol, BTable, BForm,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { required, positive } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { mapActions, mapGetters } from 'vuex'
import { modalMixins } from '@core/mixins/modal'
import { ValidationObserver } from 'vee-validate'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toAmount, toBalance } from '@/helpers/formatAmount'
import InputField from '../../../../forms/InputField.vue'
import Button from '../../../../components/button/Button.vue'
import { toDateWithYear, toDateWithTime } from '../../../../../helpers/formatDate'

export default {
  components: {
    BRow,
    BCol,
    BTable,
    BForm,
    InputField,
    Button,
    BCard,
    FullScreenSpinner,
    ValidationObserver,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [sweetAlert, tableMixins, modalMixins],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toAmount,
      toBalance,
      showFormErrorAlert: false,
      showCustomerInfo: false,
      showSearchButton: false,
      showBackButton: false,
      isEditable: true,
      isLoading: false,
      isDisabled: false,
      customerList: [],
      transactionToBeReversedList: [],
      reversalOutcomeList: [],
      required,
      positive,
      actionType: 'save',
      showSpinner: false,
      currentPage: 1,
      selected: [],
      perPage: 10,
      sortBy: 'accountName',
      searchParams: {
        postingReferenceNumber: null,
      },
      singleTransactionData: {
        referenceNumber: '',
        initiatedBy: '',
      },
      transactionToBeReversedTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'depositor', sortable: true, label: 'Depositor' },
        { key: 'initiatorID', sortable: true, label: 'Initiator ID' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'approvedBy', sortable: true, label: 'Approver' },
      ],
      reversalOutcomeTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'depositor', sortable: true, label: 'Depositor' },
        { key: 'initiatorID', sortable: true, label: 'Initiator ID' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'approvedBy', sortable: true, label: 'Approver' },
      ],
    }
  },
  computed: {
    totalEntries() {
      return this.customerList.length
    },
    numberOfPages() {
      return Math.floor((this.totalEntries + this.perPage - 1) / this.perPage)
    },
    from() {
      return (this.currentPage * this.perPage) - (this.perPage - 1)
    },
    to() {
      return Math.min(this.from + this.perPage - 1, this.totalEntries)
    },
    creditAmount() {
      return this.reversalOutcomeList.reduce((acc, val) => (acc + Number(val.credit)), 0)
    },
    ...mapGetters({
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTransactionToBeReversedTableHeaders() {
      return this.transactionToBeReversedTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedReversalOutcomeTableHeaders() {
      return this.reversalOutcomeTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  methods: {
    ...mapActions({
      getReversalPostings: 'reports/getTransactionReports',
      reversalAction: 'posting/reversalAction',
    }),
    handleSearch() {
      try {
        this.$refs.singleTransactionRules.validate().then(async success => {
          if (success) {
            this.showSpinner = true
            const response = await this.getReversalPostings({
              search: this.searchParams,
              page: this.currentPage,
              limit: this.perPage,
              route: 'getReversalPostingsInfo',
              permission: 'REVERSAL_POSTING',
            })

            const { status, data } = response
            if (status === 200 && data.recordCount > 1) {
              this.transactionToBeReversedList = data.transactionToBeReversed
              this.reversalOutcomeList = data.reversalOutcome
              this.singleTransactionData.referenceNumber = data.transactionToBeReversed[0].postingReferenceNumber
              this.showCustomerInfo = true
              this.showSearchButton = true
              this.showBackButton = true
              this.showSpinner = false
            } else {
              this.alertError({ text: this.$t('No record found') })
              this.showSpinner = false
              this.showCustomerInfo = false
              this.showSearchButton = false
              this.showBackButton = false
            }
          }
        })
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
        this.showCustomerInfo = false
        this.showSearchButton = false
      }
    },
    async handleSubmit() {
      this.confirm(this.singleTransactionAction, { text: this.$t('Are you sure you want to reverse this transaction?') })
    },
    async singleTransactionAction() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const postingApprovalModel = {
          amount: this.creditAmount,
          initiatedBy: this.userDetails.email,
          approvedBy: '',
          postingType: 'ReversalPosting',
        }
        this.singleTransactionData.postingApprovalModel = postingApprovalModel
        this.singleTransactionData.transactionToBeReversed = this.transactionToBeReversedList
        this.singleTransactionData.reversalOutcome = this.reversalOutcomeList
        this.singleTransactionData.initiatedBy = this.userDetails.email
        const response = await this.reversalAction(this.singleTransactionData)

        const { status, data } = response
        if (status === 200 && data.isSuccessful) {
          this.showCustomerInfo = false
          this.isLoading = false
          this.isDisabled = false
          this.resetInputField()
          this.resetSearchParams()
          const reversalSuccess = this.$t('Your Single transaction reversal request was successful. Single transaction reversal reference ID is')
          this.alertSuccess({ text: `${reversalSuccess} ${data.referenceNumber}` })
          this.$router.push({ name: 'single-reversal-list' })
        }
        if (status === 200 && !data.isSuccessful) {
          this.isLoading = false
          this.isDisabled = false
          this.alertError({ text: `${data.message}` })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    resetInputField() {
      this.searchParams = { ...null }
      this.depositData = { ...null }
      this.customer = { ...null }
      this.isLoading = false
      this.isDisabled = false
      this.showCustomerInfo = false
      this.showSearchButton = false
      this.showBackButton = false
    },
    showSearchField() {
      this.showSearchButton = false
      this.resetSearchParams()
    },
    resetSearchParams() {
      this.searchParams.postingReferenceNumber = null
    },
    backNavigationAction() {
      this.$router.push({
        name: 'single-reversal-list',
      })
    },
  },
}
</script>
<style scoped>
 [dir] .demo-inline-spacing > *, .custom-control , .custom-control-inline, .custom-checkbox {
    margin-top: 0rem !important;
}
.card-item {
  border: 1px solid #d8d6de !important;
  border-radius: 0.358rem
}
</style>
